const LADDER_API_URL = 'https://ladder-qmlofqf3ja-uc.a.run.app';

const DEFAULT_CAMPAIGN_ID = 'worldcup-ladder-2026';

function getHeaders(token, campaignId, locale, testToken) {
  const headers = {
    Authorization: token,
    'Content-Type': 'application/json',
    'x-campaign-id': campaignId,
    'x-locale': locale,
  };

  if (testToken) {
    headers['x-test-token'] = testToken;
  }

  return headers;
}

/**
 * @typedef {{ index: number, label: string, value: number }} LadderReward
 * @typedef {{
 *   customerId: string,
 *   rewardLabel: string,
 *   rewardValue: number,
 *   rewardIndex: number,
 *   campaignId: string,
 *   createdAt: string,
 * }} LadderPlay
 */

/**
 * Map server rewardIndex to array index in rewards pool.
 */
export function resolveWinnerArrayIndex(rewards, rewardIndex) {
  const byField = rewards.findIndex((reward) => reward.index === rewardIndex);
  if (byField >= 0) return byField;

  if (typeof rewardIndex === 'number' && rewardIndex >= 0 && rewardIndex < rewards.length) {
    return rewardIndex;
  }

  throw new Error(`Invalid rewardIndex: ${rewardIndex}`);
}

/**
 * Fetch initial game data (play status + reward pool).
 * @returns {Promise<{ canPlay: boolean, rewards: LadderReward[], play: LadderPlay | null }>}
 */
export async function fetchLadderStatus(
  token,
  campaignId = DEFAULT_CAMPAIGN_ID,
  locale = 'ko',
  testToken,
) {
  const response = await fetch(LADDER_API_URL, {
    method: 'GET',
    headers: getHeaders(token, campaignId, locale, testToken),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const body = await response.json();

  return {
    canPlay: !body.alreadyPlayedToday,
    rewards: Array.isArray(body.data) ? body.data : [],
    play: body.play ?? null,
  };
}

/**
 * Start a game — server decides winner and issues points.
 * @returns {Promise<{ play: LadderPlay }>}
 */
export async function startLadderGame(
  token,
  campaignId = DEFAULT_CAMPAIGN_ID,
  locale = 'ko',
  customerId,
  testToken,
) {
  const response = await fetch(LADDER_API_URL, {
    method: 'POST',
    headers: getHeaders(token, campaignId, locale, testToken),
    body: JSON.stringify({ customerId }),
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data?.error || `HTTP error! status: ${response.status}`);
    error.code = data?.error;
    throw error;
  }

  if (!data.success || !data.play) {
    throw new Error('Invalid ladder start response');
  }

  return { play: data.play };
}
